import { useMemo } from 'react';
import { generateCellKey, opensDividerGroup } from '../utils';
import { GroupedRow } from 'modules/pond/api/types';

export interface ProcessedRow extends GroupedRow {
  showGroup: boolean;
  groupSpan: number;
  isGroupMiddle: boolean;
  isLastInGroup: boolean;
  // The row that sits directly above a section-opening group. It draws that
  // section's top rule as its own bottom border, rather than the section's
  // first row drawing a top border: a border here replaces the hairline that
  // would be painted anyway, where a top border there would stack on top of it
  // and make the rule 1.5px thicker than the block's other edges.
  isBeforeDividerGroup: boolean;
  types: string;
}

export const useProcessedRows = (
  rows: GroupedRow[] | undefined,
  editedCells: Map<string, string>
) => {
  const baseProcessedRows = useMemo(() => {
    if (!rows) return { baseRows: [] };

    const groupStarts = new Map<string, number>();
    const groupSizes = new Map<string, number>();

    rows.forEach((row, idx) => {
      const groupKey = row.group;

      if (!groupStarts.has(groupKey)) {
        groupStarts.set(groupKey, idx);
        groupSizes.set(groupKey, 0);
      }
      groupSizes.set(groupKey, groupSizes.get(groupKey)! + 1);
    });

    const processedRows: ProcessedRow[] = rows.map((row, idx) => {
      const groupKey = row.group;

      const groupStart = groupStarts.get(groupKey)!;
      const groupSize = groupSizes.get(groupKey)!;

      const posInGroup = idx - groupStart;
      const nextRow = rows[idx + 1];

      return {
        ...row,
        showGroup: posInGroup === 0,
        groupSpan: groupSize,
        isGroupMiddle: posInGroup === Math.ceil(groupSize / 2) - 1,
        isLastInGroup: posInGroup === groupSize - 1,
        isBeforeDividerGroup:
          !!nextRow && nextRow.group !== groupKey && opensDividerGroup(nextRow.group),
        types: row.types,
      };
    });

    return { baseRows: processedRows };
  }, [rows]);

  const finalRows = useMemo(() => {
    return baseProcessedRows.baseRows.map(row => ({
      ...row,
      values: row.values.map((val, colIdx) => {
        const cellId = row.id[colIdx];
        const cellKey = generateCellKey(row.group, row.label, cellId);
        const editedValue = editedCells.get(cellKey);
        return editedValue !== undefined ? editedValue : val;
      })
    }));
  }, [baseProcessedRows.baseRows, editedCells]);

  return { flatRows: finalRows };
};
